import * as React from "react";
import * as v from "valibot";
import { merge, isEqual, cloneDeep } from "lodash";
import type { Format, CsvFormat, JsonFormat } from "@aqora-io/aqora-data-utils";
import { useStateContext } from "../state";
import { FormattedMessage, useIntl, IntlShape } from "react-intl";
import { useValibotForm, useValibotFormContext } from "@/utils/validation";
import { FieldPath } from "react-hook-form";
import {
  Form,
  FormField,
  FormControl,
  FormLabel,
  FormItem,
  FormMessage,
  FormDescription,
} from "@/kit/ui/form";
import { Switch } from "@/kit/ui/switch";
import { Input } from "@/kit/ui/input";
import {
  Select,
  SelectItem,
  SelectValue,
  SelectTrigger,
  SelectContent,
} from "@/kit/ui/select";
import { vEmptyToUndefined, vDateFormatOptions } from "./utils";
import { DateFormatOptions } from "./DateFormatOptions";

const DEFAULT_CSV_FORMAT: PrimitiveFormat = {
  format: "csv",
  has_headers: true,
  chars: {
    quote: '"',
    delimiter: ",",
    terminator: "",
    escape: "",
    comment: "",
  },
  regex: {
    null: "",
    true: "",
    false: "",
  },
  date: {
    date_fmt: "",
    timestamp_fmt: "",
  },
};

const DEFAULT_JSON_FORMAT: PrimitiveFormat = {
  format: "json",
  file: { file_type: "json", key_col: "" },
  item: { item_type: "object" },
  date: {
    date_fmt: "",
    timestamp_fmt: "",
  },
};

type PrimitiveFormat =
  | ({ format: "csv" } & Omit<CsvFormat, "regex"> & {
        regex: { null?: string; true?: string; false?: string };
      })
  | ({ format: "json" } & JsonFormat);

const primitiveFormat = (format: Format): PrimitiveFormat => {
  if ("regex" in format) {
    format.regex = {
      null: format.regex?.null?.toString(),
      true: format.regex?.true?.toString(),
      false: format.regex?.false?.toString(),
    };
  }
  return format as PrimitiveFormat;
};

const useFormat = () => {
  const { format, setFormat } = useStateContext();
  return {
    format: primitiveFormat(
      merge(DEFAULT_JSON_FORMAT, DEFAULT_CSV_FORMAT, format),
    ),
    setFormat,
  };
};

const vAsciiChar = (intl: IntlShape) =>
  v.pipe(
    v.string(),
    v.trim(),
    v.maxBytes(
      1,
      intl.formatMessage({
        defaultMessage: "Expected single ASCII character",
      }),
    ),
  );

const vRegex = (intl: IntlShape) =>
  v.pipe(
    v.string(),
    v.trim(),
    v.check(
      (value) => {
        try {
          new RegExp(value);
          return true;
        } catch (_e) {
          return false;
        }
      },
      intl.formatMessage({
        defaultMessage: "Invalid regular expression",
      }),
    ),
  );

const csvFormatSchemaFactory = (intl: IntlShape) =>
  v.object({
    format: v.literal("csv"),
    has_headers: v.optional(v.boolean()),
    chars: v.object({
      quote: v.pipe(vAsciiChar(intl), v.nonEmpty()),
      delimiter: v.pipe(vAsciiChar(intl), v.nonEmpty()),
      terminator: v.optional(v.pipe(vAsciiChar(intl), vEmptyToUndefined)),
      escape: v.optional(v.pipe(vAsciiChar(intl), vEmptyToUndefined)),
      comment: v.optional(v.pipe(vAsciiChar(intl), vEmptyToUndefined)),
    }),
    regex: v.optional(
      v.object({
        null: v.optional(v.pipe(vRegex(intl), vEmptyToUndefined)),
        true: v.optional(v.pipe(vRegex(intl), vEmptyToUndefined)),
        false: v.optional(v.pipe(vRegex(intl), vEmptyToUndefined)),
      }),
    ),
    date: v.optional(vDateFormatOptions()),
  });

const jsonFormatSchema = v.object({
  format: v.literal("json"),
  file: v.optional(
    v.variant("file_type", [
      v.object({
        file_type: v.literal("json"),
        key_col: v.optional(v.pipe(v.string(), vEmptyToUndefined)),
      }),
      v.object({ file_type: v.literal("jsonl") }),
    ]),
  ),
  item: v.optional(
    v.variant("item_type", [
      v.object({ item_type: v.literal("object") }),
      v.object({
        item_type: v.literal("list"),
        has_headers: v.optional(v.boolean()),
      }),
    ]),
  ),
  date: v.optional(vDateFormatOptions()),
});

const formatSchemaFactory = (intl: IntlShape) =>
  v.variant("format", [csvFormatSchemaFactory(intl), jsonFormatSchema]);

type FormatSchema = ReturnType<typeof formatSchemaFactory>;

const CsvFormatOptions = () => {
  const form =
    useValibotFormContext<ReturnType<typeof csvFormatSchemaFactory>>();
  return (
    <>
      <FormField
        control={form.control}
        name="has_headers"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Has headers" />
            </FormLabel>
            <FormControl>
              <Switch className="block" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="chars.delimiter"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Delimiter character" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="chars.quote"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Quote character" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="chars.terminator"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Terminator character" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormDescription>
              <FormattedMessage defaultMessage="Optional. If not provided, the default is CRLF" />
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="chars.escape"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Escape character" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="chars.comment"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Comment character" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="regex.null"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Null regex" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="regex.true"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="True regex" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <FormField
        control={form.control}
        name="regex.false"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="False regex" />
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      <DateFormatOptions />
    </>
  );
};

export const JsonFormatOptions = () => {
  const form = useValibotFormContext<typeof jsonFormatSchema>();
  const item_type = form.watch("item.item_type");
  const file_type = form.watch("file.file_type");
  return (
    <>
      <FormField
        control={form.control}
        name="file.file_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="File type" />
            </FormLabel>
            <FormControl>
              <Select {...field}>
                <SelectTrigger>
                  <SelectValue placeholder="File type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"json"}>JSON</SelectItem>
                  <SelectItem value={"jsonl"}>JSON Lines</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      {file_type === "json" && (
        <FormField
          control={form.control}
          name="file.key_col"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <FormattedMessage defaultMessage="Key column name" />
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        ></FormField>
      )}
      <FormField
        control={form.control}
        name="item.item_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="Item type" />
            </FormLabel>
            <FormControl>
              <Select {...field}>
                <SelectTrigger>
                  <SelectValue placeholder="Item type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"object"}>Object</SelectItem>
                  <SelectItem value={"list"}>List</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      {item_type === "list" && (
        <FormField
          control={form.control}
          name="item.has_headers"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <FormattedMessage defaultMessage="Has headers" />
              </FormLabel>
              <FormControl>
                <Switch className="block" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        ></FormField>
      )}
      <DateFormatOptions />
    </>
  );
};

export const FormatOptions = () => {
  const intl = useIntl();
  const { format, setFormat } = useFormat();
  const formatSchema = formatSchemaFactory(intl);
  const form = useValibotForm(formatSchema, {
    defaultValues: format,
    reValidateMode: "onSubmit",
  });
  const lastFormValue = React.useRef<v.InferInput<FormatSchema>>(format);
  const formValue = form.watch();
  React.useEffect(() => {
    if (isEqual(formValue, lastFormValue.current)) {
      return;
    }
    lastFormValue.current = cloneDeep(formValue);
    const {
      success,
      output,
      issues = [],
    } = v.safeParse(formatSchema, formValue);
    // Unfortunately, valibot resolver doesn't work well with the
    // variant schema, so we have to manually set errors.
    const newErrors = Object.fromEntries(
      issues.map((issue) => [
        v.getDotPath<FormatSchema>(issue) || "root",
        { message: issue.message },
      ]),
    );
    form.clearErrors();
    for (const [path, error] of Object.entries(newErrors)) {
      form.setError(path as FieldPath<v.InferInput<FormatSchema>>, error);
    }
    if (success) {
      setFormat(output);
    }
  }, [formatSchema, form, formValue, format, setFormat]);
  return (
    <Form form={form} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <h2 className="col-span-1 sm:col-span-2 text-l font-semibold">
        <FormattedMessage defaultMessage="Format Options" />
      </h2>
      <FormField
        control={form.control}
        name="format"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <FormattedMessage defaultMessage="File format" />
            </FormLabel>
            <FormControl>
              <Select {...field}>
                <SelectTrigger>
                  <SelectValue placeholder="Format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"csv"}>CSV</SelectItem>
                  <SelectItem value={"json"}>JSON</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      ></FormField>
      {formValue.format === "csv" && <CsvFormatOptions />}
      {formValue.format === "json" && <JsonFormatOptions />}
    </Form>
  );
};
